import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import PageTitle from "../../shared/components/UI/PageTitle";
import {
	Container,
	Grid,
	makeStyles,
	TextField,
	FormControlLabel,
	Switch,
	Button,
	Card,
	CardContent,
	Typography,
	CircularProgress,
	LinearProgress,
	Box
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import WebcrawlResults from "../components/WebcrawlResults";

const useStyles = makeStyles((theme) => ({
	formCard: {
		marginBottom: theme.spacing(3),
	},
	formRow: {
		marginBottom: theme.spacing(2),
	},
	submitButton: {
		marginTop: theme.spacing(2),
        marginLeft: theme.spacing(2),
	},
	loadingCard: {
		marginBottom: theme.spacing(3),
	},
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		padding: theme.spacing(4),
		maxWidth: 560,
		margin: '0 auto',
	},
	progressBar: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	loadingText: {
		marginTop: theme.spacing(1),
		color: theme.palette.text.secondary,
	},
	loadingStep: {
		fontSize: '0.875rem',
		color: theme.palette.primary.main,
		fontWeight: 500,
		marginBottom: theme.spacing(2),
	},
	cancelButton: {
		marginTop: theme.spacing(3),
		alignSelf: 'center',
	},
	errorAlert: {
		marginBottom: theme.spacing(2),
	}
}));

const CRAWL_STEPS = [
	"Connecting to website…",
	"Crawling pages…",
	"Extracting content…",
	"Running AI analysis…",
];

const WebCrawlScreen = (props) => {
	const classes = useStyles();
	const abortControllerRef = useRef(null);
	const [formData, setFormData] = useState({
		url: '',
		maxPages: 10,
		includeAI: true
	});
	const [formErrors, setFormErrors] = useState({});
	const [loadingStepIndex, setLoadingStepIndex] = useState(0);

	const {
		isLoading,
		data,
		error,
		onAnalyzeWebsite,
		onSetWebcrawlFormData,
		onResetWebcrawlState
	} = props;

	// Cycle through step labels while loading
	useEffect(() => {
		if (!isLoading) {
			setLoadingStepIndex(0);
			return;
		}
		const interval = setInterval(() => {
			setLoadingStepIndex((prev) => (prev + 1) % CRAWL_STEPS.length);
		}, 3000);
		return () => clearInterval(interval);
	}, [isLoading]);

	const handleInputChange = (field) => (event) => {
		const value = field === 'includeAI' ? event.target.checked : event.target.value;
		setFormData(prev => ({
			...prev,
			[field]: value
		}));
		
		// Clear error when user starts typing
		if (formErrors[field]) {
			setFormErrors(prev => ({
				...prev,
				[field]: ''
			}));
		}
	};

	const validateForm = () => {
		const errors = {};
		
		if (!formData.url.trim()) {
			errors.url = 'URL is required';
		} else if (!isValidUrl(formData.url)) {
			errors.url = 'Please enter a valid URL';
		}
		
		if (formData.maxPages < 1 || formData.maxPages > 100) {
			errors.maxPages = 'Max pages must be between 1 and 100';
		}
		
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const isValidUrl = (string) => {
		try {
			new URL(string);
			return true;
		} catch (_) {
			return false;
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		if (validateForm()) {
			abortControllerRef.current = new AbortController();
			onSetWebcrawlFormData(formData);
			onAnalyzeWebsite(formData, abortControllerRef.current.signal);
		}
	};

	const handleCancelCrawl = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
	};

	const handleReset = () => {
		setFormData({
			url: '',
			maxPages: 10,
			includeAI: true
		});
		setFormErrors({});
		onResetWebcrawlState();
	};

	return (
		<Container maxWidth="xl">
			<Grid container justify="space-between" alignItems="center">
				<Grid container alignItems="center" spacing={3}>
					<Grid item>
						<PageTitle 
							title="Web Crawl Analysis" 
							tooltip="Web Crawl Analysis"
						/>
					</Grid>
					<Grid item>
						{isLoading && <CircularProgress color="secondary" />}
					</Grid>
				</Grid>
			</Grid>

			{/* Error Alert */}
			{error && (
				<Alert severity="error" className={classes.errorAlert}>
					{error}
				</Alert>
			)}

			{/* Loading State */}
			{isLoading && (
				<Card className={classes.loadingCard}>
					<Box className={classes.loadingContainer}>
						<Typography variant="h6" className={classes.loadingText}>
							Analyzing website — this may take a few minutes
						</Typography>
						<Typography className={classes.loadingStep}>
							{CRAWL_STEPS[loadingStepIndex]}
						</Typography>
						<LinearProgress
							className={classes.progressBar}
							color="secondary"
							variant="indeterminate"
						/>
						<Typography variant="body2" color="textSecondary">
							You can cancel at any time if you no longer need this analysis.
						</Typography>
						<Button
							variant="outlined"
							color="secondary"
							onClick={handleCancelCrawl}
							className={classes.cancelButton}
						>
							Cancel crawl
						</Button>
					</Box>
				</Card>
			)}

			{/* Form */}
			{!isLoading && (
				<Card className={classes.formCard}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Website Analysis Configuration
						</Typography>
						<form onSubmit={handleSubmit}>
							<Grid container spacing={3}>
								<Grid item xs={12} md={8}>
									<TextField
										fullWidth
										label="Website URL"
										value={formData.url}
										onChange={handleInputChange('url')}
										error={!!formErrors.url}
										helperText={formErrors.url || "Enter the URL of the website to analyze"}
										placeholder="https://example.com"
										variant="outlined"
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Max Pages"
										type="number"
										value={formData.maxPages}
										onChange={handleInputChange('maxPages')}
										error={!!formErrors.maxPages}
										helperText={formErrors.maxPages || "Maximum number of pages to crawl"}
										inputProps={{ min: 1, max: 100 }}
										variant="outlined"
									/>
								</Grid>
								<Grid item xs={12}>
									<FormControlLabel
										control={
											<Switch
												checked={formData.includeAI}
												onChange={handleInputChange('includeAI')}
												color="primary"
											/>
										}
										label="Include AI Analysis"
									/>
									<Typography variant="body2" color="textSecondary">
										Enable AI-powered content analysis for deeper insights
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Box display="flex" gap={2}>
										<Button
											type="submit"
											variant="contained"
											color="primary"
											size="large"
											disabled={isLoading}
											className={classes.submitButton}
										>
											Analyze Website
										</Button>
										<Button
											variant="outlined"
											color="secondary"
											size="large"
											onClick={handleReset}
											disabled={isLoading}
											className={classes.submitButton}
										>
											Reset
										</Button>
									</Box>
								</Grid>
							</Grid>
						</form>
					</CardContent>
				</Card>
			)}

			{/* Results */}
			{data && !isLoading && <WebcrawlResults data={data} />}
		</Container>
	);
};

const mapStateToProps = (state) => ({
	isLoading: state.webcrawl.isLoading,
	data: state.webcrawl.data,
	error: state.webcrawl.error,
	formData: state.webcrawl.formData
});

const mapDispatchToProps = (dispatch) => ({
	onAnalyzeWebsite: (payload, signal) => dispatch(actionCreators.analyzeWebsite(payload, signal)),
	onSetWebcrawlFormData: (formData) => dispatch(actionCreators.setWebcrawlFormData(formData)),
	onResetWebcrawlState: () => dispatch(actionCreators.resetWebcrawlState())
});

export default connect(mapStateToProps, mapDispatchToProps)(WebCrawlScreen);
